import Head from 'next/head';
import { useTranslation } from 'next-i18next';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import ArticleCard from '@/components/blog/ArticleCard';
import ArticleCardList from '@/components/blog/ArticleCardList';
import CustomHeading from '@/components/blog/CustomHeading';
import FooterArchiveDynamicLinks from '@/components/blog/footer/FooterArchiveDynamicLinks';
import { useScreenSizeContext } from '@/components/ScreenSize.context';
import { NS } from '@/constants';
import { POSITION_TYPE } from '@/constants/blog';
import { PAGE_TYPE } from '@/constants/datalayer';
import { useAppSelector } from '@/redux';
import { useServices } from '@/services/useServices';
import { getArticleCardTypeAccordingToIndex, getBlogImageSrc } from '@/utils/blogUtils';
import styles from './HomeBlogPage.module.scss';

export const HomeBlogPage = ({ archives, topArticle, articleLines }) => {
	const { t } = useTranslation(NS.BLOG);
	const { locale } = useAppSelector(({ root }) => root.storeConf);
	const isMobile = useScreenSizeContext().isMobile;
	const { imageName, imageAlt, title, shortDesc, publishedAt, urlKeyword } = topArticle.articles[0];
	const customization = {
		customClassName: { articleCardList: styles['articles'] },
		headingLevel: { articleCardList: 2, articleCard: 3 },
		position: { articleCardList: POSITION_TYPE.START },
	};
	const { datalayerService } = useServices();

	useEffect(() => {
		datalayerService.pushPageName(PAGE_TYPE.BLOG_HOME, PAGE_TYPE.BLOG_HOME);
	}, []);

	return (
		<>
			{imageName && (
				<Head>
					<link rel="preload" href={getBlogImageSrc(imageName, isMobile)} as="image" />
				</Head>
			)}
			<div className={styles.container} data-rbf="blog-home-page">
				<CustomHeading
					title={t('homePage.title')}
					headingLevel={1}
					labelPosition={POSITION_TYPE.CENTER}
					customClassName={{ label: styles['title'] }}
				/>

				<div className={styles['top-article-section']}>
					<ArticleCard
						id={topArticle.id}
						imageSrc={getBlogImageSrc(imageName, isMobile)}
						imageAlt={imageAlt}
						tags={topArticle.tags}
						title={title}
						shortDesc={shortDesc}
						publishedAt={publishedAt}
						urlKeyword={urlKeyword}
						customization={{ headingLevel: 2 }}
						isTopArticle={true}
					/>
				</div>

				{articleLines.map((articleLine, index) => (
					<ArticleCardList
						key={index}
						title={articleLine.title}
						description={articleLine.description}
						articles={articleLine.articlesAggregation}
						articleCardType={getArticleCardTypeAccordingToIndex(index)}
						customization={customization}
					/>
				))}

				<div className={styles['footer-archive']} data-rbf="footer-archive">
					<FooterArchiveDynamicLinks archiveLinkList={archives} isMobile={isMobile} locale={locale} />
				</div>
			</div>
		</>
	);
};

HomeBlogPage.propTypes = {
	archives: PropTypes.array.isRequired,
	articleCateg: PropTypes.shape({
		homePage: PropTypes.bool,
		articleCategIdentifier: PropTypes.string,
		content: PropTypes.array.isRequired,
	}).isRequired,
	topArticle: PropTypes.object.isRequired,
	articleLines: PropTypes.array.isRequired,
};

export default HomeBlogPage;
